import { Component } from 'react';
import {
  Box,
  Button,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkError: false,
      confirmClearOpen: false,
    };
  }

  static getDerivedStateFromError(error) {
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk/.test(error?.message || '') ||
      /Failed to fetch dynamically imported module/.test(error?.message || '') ||
      /Importing a module script failed/.test(error?.message || '');

    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, isChunkError: false });

    if (this.state.isChunkError && 'caches' in window) {
      caches.keys()
        .then((names) => Promise.all(names.map((name) => caches.delete(name))))
        .catch(() => { })
        .finally(() => window.location.reload());
    } else {
      window.location.reload();
    }
  };

  handleGoBack = () => {
    this.setState({ hasError: false, error: null, isChunkError: false });
    window.history.back();
  };

  handleClearDataClick = () => {
    this.setState({ confirmClearOpen: true });
  };

  handleCancelClearData = () => {
    this.setState({ confirmClearOpen: false });
  };

  handleConfirmClearData = async () => {
    this.setState({
      hasError: false,
      error: null,
      isChunkError: false,
      confirmClearOpen: false,
    });

    localStorage.clear();

    try {
      if ('caches' in window) {
        const names = await caches.keys();

        await Promise.all(names.map((name) => caches.delete(name)));
      }
    } catch (e) { /* ignore */ }

    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();

        await Promise.all(registrations.map((reg) => reg.unregister()));
      }
    } catch (e) { /* ignore */ }

    try {
      if ('indexedDB' in window && indexedDB.databases) {
        const dbs = await indexedDB.databases();

        await Promise.all(
          dbs.map((db) => db.name && indexedDB.deleteDatabase(db.name)),
        );
      }
    } catch (e) { /* ignore */ }

    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <Box
          sx={{
            boxSizing: 'border-box',
            minHeight: '100dvh',
            width: '100%',
            maxWidth: '100vw',
            overflowX: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, sm: 3 },
            py: { xs: 4, sm: 6 },
            background: 'linear-gradient(160deg, #f1eef8 0%, #f8eef2 100%)',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              maxWidth: 420,
              borderRadius: { xs: 3, sm: 4 },
              p: { xs: 3, sm: 5 },
              textAlign: 'center',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 20px 45px -20px rgba(74, 20, 140, 0.35)',
            }}
          >
            <Stack spacing={2.5} alignItems="center">
              <Box
                sx={{
                  width: 76,
                  height: 76,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, #4A148C 0%, #880E4F 100%)',
                }}
              >
                <ErrorOutlineRoundedIcon sx={{ fontSize: 40, color: '#fff' }} />
              </Box>

              <img
                src="/logo512.png"
                alt="ClassPep"
                style={{ width: 40, height: 40, opacity: 0.8 }}
              />

              <Typography variant="h6" fontWeight={700}>
                Something went wrong
              </Typography>
              <Typography variant="body2" color="text.secondary">
                An unexpected error occurred. Please try reloading the app. If
                the problem continues, contact support.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ width: '100%' }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={this.handleGoBack}
                  sx={{ borderRadius: 2.5 }}
                >
                  Go Back
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleReload}
                  sx={{ borderRadius: 2.5 }}
                >
                  Reload App
                </Button>
              </Stack>

              <Divider sx={{ width: '100%' }} />

              <Stack spacing={0.5} alignItems="center">
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteSweepIcon />}
                  onClick={this.handleClearDataClick}
                  sx={{ borderRadius: 2.5 }}
                >
                  Clear Data
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Clearing data removes all saved app data and will log you out.
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          <Snackbar
            open={this.state.confirmClearOpen}
            onClose={this.handleCancelClearData}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            message="This will clear all app data and log you out. Continue?"
            action={
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={this.handleCancelClearData}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="error"
                  variant="contained"
                  onClick={this.handleConfirmClearData}
                >
                  Clear Data
                </Button>
              </Stack>
            }
          />
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
